import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { LocalDriverService } from '../storage/drivers/local-storage.service';
import { APP } from 'src/app/config/app';
import { AuthHttpService } from './auth.http.service';
import { lastValueFrom } from 'rxjs';
import { JWTTokenService } from './jwtToken.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private jwtHelper: JwtHelperService,
    private storage: LocalDriverService,
    private authHttpService: AuthHttpService,
    private jwtTokenService: JWTTokenService,
  ) {}

  public setCookie(name: string, value: string, days: number = 7, shared: boolean = true) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    const domainPart = window.location.hostname.includes('nemi.mobi') ? 'domain=.nemi.mobi;' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; ${shared ? domainPart : ''} path=/;`;
  }

  public getCookie(name: string): string | null {
    return document.cookie.split('; ').reduce((r, v) => {
      const parts = v.split('=');
      return parts[0] === name ? decodeURIComponent(parts.slice(1).join('=')) : r;
    }, null as string | null);
  }

  public deleteCookie(name: string, domain: string = '') {
    const expiration = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;';
    // Shared cookies only carry the domain on nemi.mobi hosts, so both variants have to be cleared.
    document.cookie = `${name}=; ${expiration}`;
    if (domain) {
      document.cookie = `${name}=; domain=${domain}; ${expiration}`;
    }
  }

  public isAuthenticated(): boolean {
    const token = this.getCookie(APP.LOCAL_STORAGE_TOKEN_KEY_NAME) || '';
    return !this.jwtHelper.isTokenExpired(token);
  }

  public setToken(token: any) {
    this.setCookie(APP.LOCAL_STORAGE_TOKEN_KEY_NAME, token, undefined, false);
    this.jwtTokenService.setToken(token);
    return this;
  }

  public setRole() {
    this.storage.set(APP.LOCAL_STORAGE_ROLE, this.jwtTokenService.getRole());
    return this;
  }

  public getToken(): string | null {
    const value = this.getCookie(APP.LOCAL_STORAGE_TOKEN_KEY_NAME) || this.storage.get(APP.LOCAL_STORAGE_TOKEN_KEY_NAME);
    return value === 'null' ? null : value;
  }

  public getRefreshToken() {
    return this.getCookie(APP.LOCAL_STORAGE_REFRESH_TOKEN) || this.storage.get(APP.LOCAL_STORAGE_REFRESH_TOKEN);
  }

  public getUserId() {
    return this.getCookie(APP.LOCAL_STORAGE_USER_ID) || this.storage.get(APP.LOCAL_STORAGE_USER_ID);
  }

  public setTokenType(type: any) {
    this.setCookie(APP.LOCAL_STORAGE_TOKEN_TYPE_KEY_NAME, type);
    return this;
  }

  public clearStorage() {
    Object.keys(localStorage).forEach((key) => {
      this.storage.remove(key);
    });
    this.deleteCookie(APP.LOCAL_STORAGE_TOKEN_KEY_NAME);
    this.deleteCookie(APP.LOCAL_STORAGE_TOKEN_TYPE_KEY_NAME);
    this.deleteCookie(APP.LOCAL_STORAGE_REFRESH_TOKEN, '.nemi.mobi');
    this.deleteCookie(APP.LOCAL_STORAGE_USER_ID, '.nemi.mobi');
  }

  async login(email: string, password: string) {
    const source$ = this.authHttpService.login(email, password);
    const response = await lastValueFrom(source$);
    return response;
  }

  async verifyCode(username: string, totp: string, ephemeralToken: string, rememberMe: boolean) {
    const source$ = this.authHttpService.verifyCode(username, totp, ephemeralToken, rememberMe);
    const response = await lastValueFrom(source$);
    return response;
  }

  async passwordReset(password: string, ephemeralToken: string) {
    const source$ = this.authHttpService.passwordReset(password, ephemeralToken);
    const response = await lastValueFrom(source$);
    return response;
  }

  async refreshToken(userId: string, refreshToken: string) {
    const source$ = this.authHttpService.refreshToken(userId, refreshToken);
    const response = await lastValueFrom(source$);
    return response;
  }

  async getEphemeralToken() {
    const source$ = this.authHttpService.getEphemeralToken();
    const response = await lastValueFrom(source$);
    return response;
  }
}
